import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { tpAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  Loader2, 
  FlaskConical,
  Clock,
  Maximize2,
  Minimize2,
  Pencil
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";

export default function TPContentPage() {
  const { workspaceId, tpId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tp, setTP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const contentRef = useRef(null);

  const canManage = user?.role === "admin" || user?.role === "trainer";

  useEffect(() => {
    loadTP();
  }, [tpId]);

  // Handle anchor clicks inside HTML content to prevent React Router from intercepting
  useEffect(() => {
    const contentEl = contentRef.current;
    if (!contentEl) return;

    const handleClick = (e) => {
      const link = e.target.closest('a');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href) return;

      // If it's an anchor link (starts with #), handle it manually
      if (href.startsWith('#')) {
        e.preventDefault();
        e.stopPropagation();
        
        const targetId = href.slice(1);
        const targetElement = contentEl.querySelector(`#${targetId}, [name="${targetId}"]`);
        
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }

      // If it's an external link, open in new tab
      if (href.startsWith('http://') || href.startsWith('https://')) {
        e.preventDefault();
        window.open(href, '_blank', 'noopener,noreferrer');
        return;
      }
    };

    contentEl.addEventListener('click', handleClick);
    return () => contentEl.removeEventListener('click', handleClick);
  }, [tp]);

  const loadTP = async () => {
    try {
      const response = await tpAPI.get(workspaceId, tpId);
      setTP(response.data);
    } catch (error) {
      console.error("Failed to load TP:", error);
      toast.error("Erreur lors du chargement du TP");
      navigate(`/workspace/${workspaceId}/tp`);
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyBadge = (difficulty) => {
    const badges = {
      easy: <Badge className="bg-green-500">Facile</Badge>,
      medium: <Badge className="bg-yellow-500">Moyen</Badge>,
      hard: <Badge className="bg-red-500">Difficile</Badge>,
    };
    return badges[difficulty] || <Badge>{difficulty}</Badge>;
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  // Process HTML content to ensure iframe links stay within iframe
  // Adds sandbox attributes and scripts to handle internal navigation
  const processHtmlContent = (content) => {
    if (!content) return "<p class='text-muted-foreground'>Aucun contenu disponible.</p>";
    
    // Script to inject in srcdoc to handle anchor links properly
    const anchorScript = `<script>
      document.addEventListener('click', function(e) {
        var link = e.target.closest('a');
        if (!link) return;
        var href = link.getAttribute('href');
        if (!href) return;
        
        // Handle anchor links
        if (href.startsWith('#')) {
          e.preventDefault();
          e.stopPropagation();
          var targetId = href.slice(1);
          var target = document.getElementById(targetId) || document.querySelector('[name="' + targetId + '"]');
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return false;
        }
        
        // Handle external links - open in new tab
        if (href.startsWith('http://') || href.startsWith('https://')) {
          e.preventDefault();
          window.open(href, '_blank', 'noopener,noreferrer');
          return false;
        }
      }, true);
    <\/script>`;
    
    // Find all iframes with srcdoc and add navigation script to their content
    return content.replace(/(<iframe[^>]*srcdoc=["'])([^"']*)(["'][^>]*>)/gi, (match, prefix, srcdoc, suffix) => {
      // Decode HTML entities in srcdoc
      let decodedSrcdoc = srcdoc
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
      
      // Add script to handle navigation if not already present
      if (!decodedSrcdoc.includes('data-nav-handler')) {
        // Mark that we've added the handler
        const markedScript = anchorScript.replace('<script>', '<script data-nav-handler="true">');
        
        // Add base target and script
        if (decodedSrcdoc.includes('</body>')) {
          decodedSrcdoc = decodedSrcdoc.replace('</body>', markedScript + '</body>');
        } else if (decodedSrcdoc.includes('</html>')) {
          decodedSrcdoc = decodedSrcdoc.replace('</html>', markedScript + '</html>');
        } else {
          decodedSrcdoc = decodedSrcdoc + markedScript;
        }
        
        // Also add base target for good measure
        if (!decodedSrcdoc.includes('<base')) {
          if (decodedSrcdoc.includes('<head>')) {
            decodedSrcdoc = decodedSrcdoc.replace('<head>', '<head><base target="_self">');
          } else if (decodedSrcdoc.includes('<head ')) {
            decodedSrcdoc = decodedSrcdoc.replace(/<head([^>]*)>/, '<head$1><base target="_self">');
          } else if (decodedSrcdoc.includes('<html')) {
            decodedSrcdoc = decodedSrcdoc.replace(/<html([^>]*)>/i, '<html$1><head><base target="_self"></head>');
          }
        }
      }
      
      // Re-encode for srcdoc attribute
      const encodedSrcdoc = decodedSrcdoc
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
      
      return prefix + encodedSrcdoc + suffix;
    });
  };

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </WorkspaceLayout>
    );
  }

  if (!tp) {
    return null;
  }

  return (
    <WorkspaceLayout>
      <div className={`animate-fade-in ${isFullscreen ? 'fixed inset-0 z-50 bg-background p-6 overflow-auto' : 'page-container'}`}>
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/workspace/${workspaceId}/tp`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900/20 shrink-0">
            <FlaskConical className="h-6 w-6 text-orange-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold tracking-tight">{tp.title}</h1>
              {getDifficultyBadge(tp.difficulty)}
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {tp.duration_minutes} minutes
              </span>
              {tp.chapter && <span>Chapitre: {tp.chapter}</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="icon" onClick={toggleFullscreen}>
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
            {canManage && (
              <Button variant="outline" onClick={() => navigate(`/workspace/${workspaceId}/tp/${tpId}`)}>
                <Pencil className="h-4 w-4 mr-2" />
                Modifier
              </Button>
            )}
          </div>
        </div>

        {/* Description */}
        {tp.description && (
          <p className="text-muted-foreground mb-6 text-lg">{tp.description}</p>
        )}

        {/* Content */}
        <div className="border rounded-lg bg-card">
          <style>{`
            .tp-content iframe[data-html-block-iframe] {
              min-height: 2000px !important;
              height: auto !important;
            }
            .tp-content [data-html-block] iframe {
              min-height: 2000px !important;
              height: auto !important;
            }
            .tp-content div.my-4 > iframe:not([data-drive-video-iframe]) {
              min-height: 2000px !important;
              height: auto !important;
            }
            .tp-content [data-drive-video] iframe,
            .tp-content iframe[data-drive-video-iframe] {
              min-height: 450px !important;
              height: 450px !important;
            }
          `}</style>
          <div 
            ref={contentRef}
            className="tp-content prose prose-lg dark:prose-invert max-w-none p-8"
            dangerouslySetInnerHTML={{ __html: processHtmlContent(tp.content) }}
          />
        </div>

        {/* Related TPs */}
        {tp.related_tps && tp.related_tps.length > 0 && (
          <RelatedTPs workspaceId={workspaceId} tpIds={tp.related_tps} />
        )}
      </div>
    </WorkspaceLayout>
  );
}

// Component to display related TPs
function RelatedTPs({ workspaceId, tpIds }) {
  const [relatedTPs, setRelatedTPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadRelatedTPs = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/workspaces/${workspaceId}/tp`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (response.ok) {
          const allTPs = await response.json();
          setRelatedTPs(allTPs.filter(tp => tpIds.includes(tp.id)));
        }
      } catch (error) {
        console.error('Failed to load related TPs:', error);
      } finally {
        setLoading(false);
      }
    };
    loadRelatedTPs();
  }, [workspaceId, tpIds]);

  if (loading || relatedTPs.length === 0) return null;

  return (
    <div className="mt-8 pt-8 border-t">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <FlaskConical className="h-5 w-5" />
        TPs liés
      </h3>
      <div className="grid gap-4 md:grid-cols-2">
        {relatedTPs.map(tp => (
          <div
            key={tp.id}
            onClick={() => navigate(`/workspace/${workspaceId}/tp/${tp.id}/view`)}
            className="p-4 border rounded-lg cursor-pointer hover:bg-muted transition-colors"
          >
            <h4 className="font-medium">{tp.title}</h4>
            {tp.description && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{tp.description}</p>
            )}
            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              {tp.duration_minutes} min
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
